import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, CalendarPlus, CheckCircle2, RefreshCw, Video } from 'lucide-react';
import consultationsApi from 'api/requests/consultations';
import Loader from 'components/_UI/Loader';
import ResponsiveModal from 'components/_UI/ResponsiveModal';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { dayParts, formatDay, formatWhen, googleCalendarUrl, initials, shortDay } from './calendar';

const NOTES_MAX_LENGTH = 1000;

const FALLBACK_ERROR = 'Não foi possível agendar agora. Tente novamente em instantes.';

/** Primeira mensagem de erro de uma resposta 422 da API (a de validação vem em `errors`). */
function messageFrom(error) {
    const data = error?.response?.data;
    const firstValidation = data?.errors && Object.values(data.errors)[0]?.[0];

    return data?.reason ? data.message : (firstValidation || FALLBACK_ERROR);
}

/**
 * Agendar a consulta grátis: o produtor escolhe o consultor (quando há mais de
 * um), o dia e o horário livre, e diz sobre o que quer conversar. Os horários
 * vêm da API; se alguém levar o horário antes, a lista é recarregada.
 */
export default function ScheduleConsultationModal({ isOpen, topics, onClose, onBooked }) {
    const [slots, setSlots] = useState({ loading: true, failed: false, slotMinutes: 30, consultants: [] });
    const [consultantId, setConsultantId] = useState(null);
    const [day, setDay] = useState(null);
    const [startsAt, setStartsAt] = useState(null);
    const [topic, setTopic] = useState(null);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [booking, setBooking] = useState(null);

    const loadSlots = useCallback(async () => {
        setSlots(current => ({ ...current, loading: true, failed: false }));

        try {
            const { data } = await consultationsApi.slots();
            setSlots({ loading: false, failed: false, slotMinutes: data.data.slot_minutes, consultants: data.data.consultants });
        } catch (requestError) {
            setSlots(current => ({ ...current, loading: false, failed: true }));
        }
    }, []);

    // Cada abertura começa do zero, com os horários de agora.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setConsultantId(null);
        setDay(null);
        setStartsAt(null);
        setTopic(null);
        setNotes('');
        setError(null);
        setBooking(null);
        setSubmitting(false);
        loadSlots();
    }, [isOpen, loadSlots]);

    const consultant = slots.consultants.find(item => item.id === consultantId) || slots.consultants[0] || null;
    const dayEntry = consultant?.days.find(item => item.date === day) || consultant?.days[0] || null;
    const slot = dayEntry?.slots.find(item => item.starts_at === startsAt) || null;

    function confirmLabel() {
        if (submitting) {
            return 'Agendando…';
        }

        return slot ? `Confirmar consulta · ${shortDay(dayEntry.date)} às ${slot.time}` : 'Escolha um horário';
    }

    function chooseConsultant(id) {
        setConsultantId(id);
        setDay(null);
        setStartsAt(null);
    }

    function chooseDay(date) {
        setDay(date);
        setStartsAt(null);
    }

    async function submit() {
        if (!consultant || !slot || submitting) {
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const { data } = await consultationsApi.book({
                consultant_id: consultant.id,
                starts_at: slot.starts_at,
                topic: topic || undefined,
                notes: notes.trim() || undefined,
            });

            setBooking(data.data);
            onBooked(data.data);
        } catch (requestError) {
            setError(messageFrom(requestError));

            if (requestError?.response?.data?.reason === 'slot_unavailable') {
                setStartsAt(null);
                loadSlots();
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={submitting ? () => false : onClose}
            title={booking ? 'Consulta agendada' : 'Agendar consulta grátis'}
            size='large'
        >
            <Content>
                {booking ? (
                    <Confirmation booking={booking} onClose={onClose} />
                ) : (
                    <>
                        <Intro>
                            Escolha um horário para conversar com um especialista da Whizr sobre o seu perfil e as suas vendas.
                            A consulta dura {slots.slotMinutes} minutos, é online e não custa nada.
                        </Intro>

                        {slots.loading && !slots.consultants.length && <Centered><Loader /></Centered>}

                        {slots.failed && (
                            <EmptyState role='alert'>
                                <AlertCircle size={22} aria-hidden='true' />
                                <strong>Não foi possível carregar os horários.</strong>
                                <GhostButton type='button' onClick={loadSlots}>
                                    <RefreshCw size={14} aria-hidden='true' /> Tentar de novo
                                </GhostButton>
                            </EmptyState>
                        )}

                        {!slots.loading && !slots.failed && !consultant && (
                            <EmptyState>
                                <AlertCircle size={22} aria-hidden='true' />
                                <strong>Não há horários livres nos próximos dias.</strong>
                                <span>Abrimos novos horários com frequência. Volte em breve.</span>
                                <GhostButton type='button' onClick={loadSlots}>
                                    <RefreshCw size={14} aria-hidden='true' /> Atualizar
                                </GhostButton>
                            </EmptyState>
                        )}

                        {consultant && (
                            <>
                                <Consultants
                                    consultants={slots.consultants}
                                    selected={consultant}
                                    onSelect={chooseConsultant}
                                />

                                <Group>
                                    <GroupLabel>Dia</GroupLabel>
                                    <Days role='group' aria-label='Dia da consulta'>
                                        {consultant.days.map(item => {
                                            const parts = dayParts(item.date);

                                            return (
                                                <DayButton
                                                    key={item.date}
                                                    type='button'
                                                    $selected={dayEntry?.date === item.date}
                                                    aria-pressed={dayEntry?.date === item.date}
                                                    aria-label={formatDay(item.date)}
                                                    onClick={() => chooseDay(item.date)}
                                                >
                                                    <span>{parts.weekday}</span>
                                                    <strong>{parts.day}</strong>
                                                    <span>{parts.month}</span>
                                                </DayButton>
                                            );
                                        })}
                                    </Days>
                                </Group>

                                <Group>
                                    <GroupLabel>Horário</GroupLabel>
                                    <Times role='group' aria-label='Horário da consulta'>
                                        {dayEntry?.slots.map(item => (
                                            <TimeButton
                                                key={item.starts_at}
                                                type='button'
                                                $selected={startsAt === item.starts_at}
                                                aria-pressed={startsAt === item.starts_at}
                                                onClick={() => setStartsAt(item.starts_at)}
                                            >
                                                {item.time}
                                            </TimeButton>
                                        ))}
                                    </Times>
                                </Group>

                                {topics.length > 0 && (
                                    <Group>
                                        <GroupLabel>Sobre o que você quer conversar? (opcional)</GroupLabel>
                                        <Topics role='group' aria-label='Assunto'>
                                            {topics.map(item => (
                                                <TopicChip
                                                    key={item.key}
                                                    type='button'
                                                    $selected={topic === item.key}
                                                    aria-pressed={topic === item.key}
                                                    onClick={() => setTopic(topic === item.key ? null : item.key)}
                                                >
                                                    {item.label}
                                                </TopicChip>
                                            ))}
                                        </Topics>
                                    </Group>
                                )}

                                <Group>
                                    <GroupLabel as='label' htmlFor='consultation-notes'>
                                        Quer adiantar algo para o especialista? (opcional)
                                    </GroupLabel>
                                    <Notes
                                        id='consultation-notes'
                                        rows={3}
                                        maxLength={NOTES_MAX_LENGTH}
                                        placeholder='Ex.: o próximo evento tem só 40% dos ingressos vendidos e quero entender o que mudar.'
                                        value={notes}
                                        onChange={event => setNotes(event.target.value)}
                                    />
                                </Group>
                            </>
                        )}

                        {error && <ErrorText role='alert'>{error}</ErrorText>}

                        {consultant && (
                            <Footer>
                                <SubmitButton type='button' disabled={!slot || submitting} onClick={submit}>
                                    {confirmLabel()}
                                </SubmitButton>
                                <Footnote>É grátis. Você recebe a confirmação por e-mail e pode cancelar até a hora da consulta.</Footnote>
                            </Footer>
                        )}
                    </>
                )}
            </Content>
        </ResponsiveModal>
    );
}

function Consultants({ consultants, selected, onSelect }) {
    return (
        <Group>
            <GroupLabel>{consultants.length > 1 ? 'Com quem' : 'Seu especialista'}</GroupLabel>

            <ConsultantList>
                {consultants.map(item => {
                    const isSelected = item.id === selected.id;

                    return (
                        <ConsultantCard
                            key={item.id}
                            as={consultants.length > 1 ? 'button' : 'div'}
                            type={consultants.length > 1 ? 'button' : undefined}
                            $selected={isSelected}
                            $clickable={consultants.length > 1}
                            aria-pressed={consultants.length > 1 ? isSelected : undefined}
                            onClick={() => consultants.length > 1 && onSelect(item.id)}
                        >
                            <Avatar $photo={item.photo_url} aria-hidden='true'>{!item.photo_url && initials(item.name)}</Avatar>
                            <div>
                                <ConsultantName>{item.name}</ConsultantName>
                                {item.title && <ConsultantTitle>{item.title}</ConsultantTitle>}
                            </div>
                        </ConsultantCard>
                    );
                })}
            </ConsultantList>

            {selected.bio && <Bio>{selected.bio}</Bio>}
        </Group>
    );
}

function Confirmation({ booking, onClose }) {
    return (
        <ConfirmationWrap>
            <ConfirmationIcon><CheckCircle2 size={30} aria-hidden='true' /></ConfirmationIcon>
            <ConfirmationTitle>Tudo certo!</ConfirmationTitle>
            <ConfirmationWhen>{formatWhen(booking.starts_at)}</ConfirmationWhen>
            <Intro>
                Sua consulta com {booking.consultant.name} está marcada. Enviamos a confirmação e o convite de calendário
                para o seu e-mail.
            </Intro>

            <ConfirmationActions>
                {booking.meeting_url && (
                    <PrimaryLink href={booking.meeting_url} target='_blank' rel='noopener noreferrer'>
                        <Video size={15} aria-hidden='true' /> Link da reunião
                    </PrimaryLink>
                )}
                <SecondaryLink href={googleCalendarUrl(booking)} target='_blank' rel='noopener noreferrer'>
                    <CalendarPlus size={15} aria-hidden='true' /> Adicionar ao Google Agenda
                </SecondaryLink>
                <GhostButton type='button' onClick={onClose}>Fechar</GhostButton>
            </ConfirmationActions>
        </ConfirmationWrap>
    );
}

const accent = panelAccent(colors.secondaryPurple);

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: left;
`;

const Intro = styled.p`
    margin: 0 0 16px 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.night};
`;

const Centered = styled.div`
    display: flex;
    justify-content: center;
    padding: 32px 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 24px 16px;
    margin-bottom: 12px;
    border-radius: 12px;
    background: ${colors.neutral};
    text-align: center;
    font-size: 14px;
    color: ${colors.night};

    strong {
        color: ${colors.nightDark};
    }
`;

const Group = styled.div`
    margin-bottom: 16px;
`;

const GroupLabel = styled.span`
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const ConsultantList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ConsultantCard = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1.5px solid ${({ $selected }) => ($selected ? accent : colors.neutralDark)};
    background: ${({ $selected }) => ($selected ? panelAccentAlpha(colors.secondaryPurple, '0D') : colors.neutralLight)};
    font-family: inherit;
    text-align: left;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
    transition: border-color 0.15s ease, background 0.15s ease;
`;

const Avatar = styled.span`
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${({ $photo }) => ($photo ? `center / cover no-repeat url(${$photo})` : panelAccentAlpha(colors.secondaryPurple, '1F'))};
    color: ${accent};
    font-size: 15px;
    font-weight: 700;
`;

const ConsultantName = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ConsultantTitle = styled.span`
    display: block;
    font-size: 13px;
    color: ${colors.night};
`;

const Bio = styled.p`
    margin: 10px 0 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
`;

const Days = styled.div`
    display: flex;
    gap: 8px;
    padding-bottom: 4px;
    overflow-x: auto;
    scrollbar-width: thin;
`;

const DayButton = styled.button`
    flex: 0 0 auto;
    width: 62px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 8px 0;
    border-radius: 12px;
    border: 1.5px solid ${({ $selected }) => ($selected ? accent : colors.neutralDark)};
    background: ${({ $selected }) => ($selected ? accent : colors.neutralLight)};
    color: ${({ $selected }) => ($selected ? colors.neutralLight : colors.night)};
    font-family: inherit;
    font-size: 12px;
    text-transform: capitalize;
    cursor: pointer;
    transition: all 0.15s ease;

    strong {
        font-size: 18px;
        font-weight: 700;
        color: ${({ $selected }) => ($selected ? colors.neutralLight : colors.nightDark)};
    }

    &:hover {
        border-color: ${accent};
    }
`;

const Times = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
    gap: 8px;
`;

const TimeButton = styled.button`
    padding: 10px 0;
    border-radius: 10px;
    border: 1.5px solid ${({ $selected }) => ($selected ? accent : colors.neutralDark)};
    background: ${({ $selected }) => ($selected ? accent : colors.neutralLight)};
    color: ${({ $selected }) => ($selected ? colors.neutralLight : colors.nightDark)};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: ${accent};
    }
`;

const Topics = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const TopicChip = styled.button`
    padding: 8px 12px;
    border-radius: 999px;
    border: 1.5px solid ${({ $selected }) => ($selected ? accent : colors.neutralDark)};
    background: ${({ $selected }) => ($selected ? panelAccentAlpha(colors.secondaryPurple, '14') : colors.neutralLight)};
    color: ${({ $selected }) => ($selected ? accent : colors.night)};
    font-family: inherit;
    font-size: 13px;
    font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
    cursor: pointer;
    transition: all 0.15s ease;
`;

const Notes = styled.textarea`
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    font-family: inherit;
    font-size: 14px;
    color: ${colors.nightDark};
    resize: vertical;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
        border-color: ${accent};
    }
`;

const ErrorText = styled.p`
    margin: 0 0 10px 0;
    font-size: 13px;
    text-align: center;
    color: ${colors.danger};
`;

/* Fica à vista no fim da rolagem do modal: o botão não some atrás de uma lista
   longa de horários. A sombra branca cobre o respiro (padding) do fim do modal,
   por onde o conteúdo rolado apareceria. */
const Footer = styled.div`
    position: sticky;
    bottom: 0;
    margin-top: 4px;
    padding: 12px 0 2px;
    border-top: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    box-shadow: 0 48px 0 0 ${colors.neutralLight};
`;

const SubmitButton = styled.button`
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: ${accent};
    color: ${colors.neutralLight};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        opacity: 0.92;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const Footnote = styled.p`
    margin: 10px 0 0 0;
    font-size: 12px;
    text-align: center;
    color: ${colors.nightLight};
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: transparent;
    color: ${colors.night};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral};
    }
`;

const ConfirmationWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 8px 0;
`;

const ConfirmationIcon = styled.div`
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    border-radius: 50%;
    background: #dcfce7;
    color: #166534;
`;

const ConfirmationTitle = styled.h4`
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ConfirmationWhen = styled.p`
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: ${accent};
`;

const ConfirmationActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-top: 4px;
`;

const linkStyles = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 16px;
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
`;

const PrimaryLink = styled.a`
    ${linkStyles}
    background: ${accent};
    color: ${colors.neutralLight};
`;

const SecondaryLink = styled.a`
    ${linkStyles}
    border: 1.5px solid ${accent};
    color: ${accent};
`;
